/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { EntitySelectors } from '@reduxjs/toolkit';
import { createSelector } from '@reduxjs/toolkit';
import { chain } from 'lodash';
import type { TimeSeriesService } from '@opengeoweb/shared';
import {
  genericSelectors,
  selectorMemoizationOptions,
} from '@opengeoweb/store';
import type {
  syncGroupsTypes,
  CoreAppStore,
  layerTypes,
} from '@opengeoweb/store';
import {
  timeseriesSelectServiceFilterChipsAdapter,
  initialPanelState,
} from './reducer';
import type {
  ServiceFilterChipsObject,
  ServiceFilterChipsObjectEntities,
  ServiceFilterChipsType,
  TimeSeriesPreset,
  TimeSeriesServicePopupObject,
  TimeSeriesStoreType,
  TimeSeriesState,
} from './types';
import type {
  GeoJsonWithService,
  Parameter,
  Plot,
  PlotPreset,
  TimeSeriesLocation,
} from '../components/TimeSeries/types';
import { getServiceById } from '../utils/edrUtils';

interface TimeSeriesStateSlice {
  timeSeries?: TimeSeriesState | TimeSeriesStoreType;
}

export const getPanelState = (
  state: TimeSeriesStateSlice,
  panelId?: string,
): TimeSeriesStoreType | undefined => {
  const timeSeriesState = state.timeSeries;
  if (!timeSeriesState) {
    return undefined;
  }
  if ('byId' in timeSeriesState) {
    if (!panelId) {
      return undefined;
    }
    return timeSeriesState.byId?.[panelId];
  }
  return timeSeriesState;
};

export const getPlotState = (
  state: TimeSeriesStateSlice,
  panelId?: string,
): PlotPreset | undefined => {
  return getPanelState(state, panelId)?.plotPreset;
};

export const getTimeSeriesState = (
  state: TimeSeriesStateSlice,
  panelId?: string,
): TimeSeriesStoreType | undefined => {
  return getPanelState(state, panelId);
};

export const getMapId = (
  state: TimeSeriesStateSlice,
  panelId?: string,
): string | undefined => getPanelState(state, panelId)?.plotPreset?.mapId;

export const getPlotWithParameters = createSelector(
  [
    (
      state: TimeSeriesStateSlice,
      _selectPlotId: string,
      panelId?: string,
    ): PlotPreset | undefined => getPlotState(state, panelId),
    (_: TimeSeriesStateSlice, selectPlotId: string): string => selectPlotId,
  ],
  (plotPreset, selectPlotId: string): Plot | undefined => {
    if (!plotPreset || !selectPlotId) {
      return undefined;
    }

    const plot = plotPreset.plots.find((plot) => plot.plotId === selectPlotId);
    if (!plot) {
      return undefined;
    }

    const parameters = plotPreset.parameters.filter(
      (parameter) => parameter.plotId === selectPlotId,
    );

    return { ...plot, parameters };
  },
);

export const getService = createSelector(
  [
    (
      state: TimeSeriesStateSlice,
      _serviceId: string | undefined,
      _panelId?: string,
    ): TimeSeriesState | TimeSeriesStoreType | undefined => state.timeSeries,
    (
      _: TimeSeriesStateSlice,
      serviceId: string | undefined,
    ): string | undefined => serviceId,
  ],
  (
    timeSeriesState: TimeSeriesState | TimeSeriesStoreType | undefined,
    serviceId: string | undefined,
  ): TimeSeriesService | undefined => {
    if (!timeSeriesState || !('byId' in timeSeriesState)) {
      return undefined;
    }
    return getServiceById(timeSeriesState.services || [], serviceId);
  },
);

export const getServices = createSelector(
  [
    (
      state: TimeSeriesStateSlice,
      _panelId?: string,
    ): TimeSeriesState | TimeSeriesStoreType | undefined => state.timeSeries,
  ],
  (
    timeSeriesState: TimeSeriesState | TimeSeriesStoreType | undefined,
  ): TimeSeriesService[] | undefined => {
    if (!timeSeriesState) {
      return [];
    }
    if ('byId' in timeSeriesState) {
      return timeSeriesState.services;
    }
    return [];
  },
  selectorMemoizationOptions,
);

/**
 * Returns search filter string
 *
 * Example getSearchFilter(store);
 * @param {object} store store: object - store object
 * @returns {array} returnType: string
 */
export const getSearchFilter = createSelector(
  getTimeSeriesState,
  (store): string => store?.timeseriesSelect?.filters?.searchFilter || '',
);

/**
 * Returns all currently used parameters
 *
 * Example getParametersInUse(store);
 * @param {object} store store: object - store object
 * @returns {Parameter[]} returnType: Parameter[]
 */
export const getParametersInUse = createSelector(
  getTimeSeriesState,
  (store): Parameter[] => store?.plotPreset?.parameters || [],
);

/**
 * Returns active services array
 *
 * Example getActiveServices(store);
 * @param {object} store store: object - store object
 * @returns {ActiveServiceObjectEntities} returnType: ActiveServiceObjectEntitiesobject of active services
 */
// cast to usable type - selectEntities returns Dictionary<ActiveServiceObject> which is not usable inside of the code
const getServiceFilterChipsState = (
  store: TimeSeriesStateSlice,
  panelId?: string,
): ServiceFilterChipsType => {
  return (
    getTimeSeriesState(store, panelId)?.timeseriesSelect?.filters
      ?.serviceFilterChips || {
      entities: {},
      ids: [],
    }
  );
};

const makeServiceFilterChipsSelectors = (
  panelId?: string,
): EntitySelectors<ServiceFilterChipsObject, TimeSeriesStateSlice, string> =>
  timeseriesSelectServiceFilterChipsAdapter.getSelectors(
    (store: TimeSeriesStateSlice): ServiceFilterChipsType =>
      getServiceFilterChipsState(store, panelId),
  );

export const getServiceFilterChips = (
  store: TimeSeriesStateSlice,
  panelId?: string,
): ServiceFilterChipsObjectEntities =>
  makeServiceFilterChipsSelectors(panelId).selectEntities(
    store,
  ) as ServiceFilterChipsObjectEntities;

export const getServiceFilterChipsById = (
  store: TimeSeriesStateSlice,
  id: string,
  panelId?: string,
): ServiceFilterChipsObject | undefined =>
  makeServiceFilterChipsSelectors(panelId).selectById(store, id);

/**
 * Returns whether all service filter chips are enabled
 *
 * Example getAllFilterChipsEnabled(store);
 * @param {object} store store: object - store object
 * @returns {boolean} returnType: boolean
 */
export const getAllFilterChipsEnabled = createSelector(
  getTimeSeriesState,
  (store): boolean =>
    store?.timeseriesSelect?.filters?.allServiceFilterChipsEnabled || false,
);
/**
 * Returns current parameter id displayed in the info dialog
 *
 * Example getCurrentParameterInfoDisplayed(store);
 * @param {object} store store: object - store object
 * @returns {string} returnType: string
 */
export const getCurrentParameterInfoDisplayed = createSelector(
  getTimeSeriesState,
  (store): string =>
    typeof store?.timeseriesSelect?.currentParameterInfo === 'string'
      ? store.timeseriesSelect.currentParameterInfo
      : '',
);

/**
 * Returns parameter for the parameterInfo window
 *
 * Example getParameterInfoParameter(store);
 * @param {object} store store: object - store object
 * @returns {Parameter} returnType: Parameter
 */
export const getParameterInfoParameter = createSelector(
  getTimeSeriesState,
  (store: TimeSeriesStoreType | undefined): Parameter | undefined => {
    if (typeof store?.timeseriesSelect?.currentParameterInfo === 'string') {
      if (store.plotPreset) {
        return store.plotPreset.parameters?.find(
          (parameter) =>
            parameter.id === store!.timeseriesSelect!.currentParameterInfo,
        );
      }
      return undefined;
    }
    return store?.timeseriesSelect?.currentParameterInfo;
  },
);

/**
 * Returns parameter by id
 *
 * Example getParameterById(store);
 * @param {object} store store: object - store object
 * @param {string} parameterId parameterId
 * @returns {Parameter} returnType: Parameter
 */
export const getParameterById = createSelector(
  [
    (
      store: TimeSeriesStateSlice,
      _parameterId: string,
      panelId?: string,
    ): TimeSeriesStoreType | undefined => getTimeSeriesState(store, panelId),
    (store: TimeSeriesStateSlice, parameterId: string): string => parameterId,
  ],
  (
    store: TimeSeriesStoreType | undefined,
    parameterId: string,
  ): Parameter | undefined => {
    if (store?.plotPreset) {
      return store.plotPreset.parameters?.find(
        (parameter) => parameter.id === parameterId,
      );
    }
    return undefined;
  },
);

export const getServicePopupDetails = createSelector(
  getTimeSeriesState,
  (store): TimeSeriesServicePopupObject =>
    store?.timeseriesSelect?.servicePopup ||
    initialPanelState.timeseriesSelect!.servicePopup,
);

export const getTimeSeriesPreset = createSelector(
  getPlotState,
  getServices,
  (plotState, services): TimeSeriesPreset | undefined => {
    if (!plotState || !services) {
      return undefined;
    }
    const presetParameters = plotState.parameters.map((parameter) => {
      const presetParam = { ...parameter };
      const isRelativeReferenceTime = /^latest(?:-\d+)?$/.test(
        presetParam.instanceId || '',
      );
      if (!isRelativeReferenceTime) {
        delete presetParam.instanceId;
      }
      return presetParam;
    });
    const usedServicesIds = chain(presetParameters)
      .keyBy((param) => param.serviceId)
      .mapValues(() => true)
      .value();
    const usedServices = services.filter(
      (service) => usedServicesIds[service.id],
    );

    return {
      plotPreset: {
        ...plotState,
        parameters: presetParameters,
      },
      services: usedServices,
      syncGroupsIds: [],
    };
  },
);

export const getGeoJSONWithService = createSelector(
  [
    getServices,
    (state, panelId): string => panelId as string,
    (state: CoreAppStore, panelId): layerTypes.ReduxLayer | undefined =>
      genericSelectors.selectFeatureSyncLayersForPanel(
        state,
        panelId as string,
      )[0],
  ],
  (services, _panelId, firstFeatureLayer): GeoJsonWithService[] => {
    const geoJsonFeatures = firstFeatureLayer?.geojson?.features ?? [];
    const locationServicesObj = geoJsonFeatures.reduce<
      Record<string, GeoJsonWithService>
    >((list: Record<string, GeoJsonWithService>, feature) => {
      const collectionList = { ...list };
      feature.properties?.occurrences?.forEach(
        (occurrence: syncGroupsTypes.Occurrence) => {
          const { collectionId, serviceId } = occurrence.properties;
          // create new collection if it does not exist
          if (!collectionList[collectionId]) {
            collectionList[collectionId] = {
              collectionName: occurrence.properties.collectionId,
              serviceName:
                services?.find((service) => service.id === serviceId)?.name ||
                '',
              geoJson: {
                type: 'FeatureCollection',
                features: [],
              },
            };
          }
          collectionList[collectionId].geoJson.features.push(feature);
        },
      );
      return collectionList;
    }, {});
    return Object.values(locationServicesObj);
  },
  selectorMemoizationOptions,
);

export const getIsMapPinDisabled = createSelector(
  getTimeSeriesState,
  (store): boolean => store?.locationData?.isMapPinDisabled || false,
);

export const getShouldHideLocationMarkers = createSelector(
  getTimeSeriesState,
  (store): boolean => store?.locationData?.shouldHideLocationMarkers || false,
);

export const getIsLegendVisible = createSelector(
  getTimeSeriesState,
  (store): boolean => store?.locationData?.isLegendVisible ?? true,
);

export const getCurrentLocation = createSelector(
  getTimeSeriesState,
  (store): TimeSeriesLocation | undefined =>
    store?.locationData?.currentLocation,
);
